import { Folder, ExpandMore, ChevronRight } from '@mui/icons-material';
import { Box, Divider, List, ListItem, ListItemButton, ListItemText, Stack } from '@mui/material';
import { TreeItem, TreeView } from '@mui/lab';
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { RoutePaths } from '../../RouteConfig';
import {
  ClassificationContext,
  ClassificationGroupMap,
} from '../context/ClassificationContext.types';

const EXPANDED_NODES_KEY = 'classification-sidebar-expanded-nodes';

export function ClassificationSidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  const { groupMap, isGroupsLoading } = useContext(ClassificationContext);

  useEffect(() => {
    const savedExpanded = localStorage.getItem(EXPANDED_NODES_KEY);
    if (savedExpanded) {
      try {
        setExpandedNodes(JSON.parse(savedExpanded));
      } catch {
        setExpandedNodes([]);
      }
    }
  }, []);

  const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    // Nur erlauben wenn es ein Aufklappen ist (mehr Knoten als vorher)
    if (nodeIds.length > expandedNodes.length) {
      setExpandedNodes(nodeIds);
      localStorage.setItem(EXPANDED_NODES_KEY, JSON.stringify(nodeIds));
    }
  };
  const handleNodeClick = (event: React.SyntheticEvent, nodeId: string) => {
    navigate(RoutePaths.CLASSIFICATION_GROUPS + '/' + nodeId);
  };

  const handleChevronClick = (event: React.MouseEvent, nodeId: string) => {
    event.stopPropagation();
    const isCurrentlyExpanded = expandedNodes.includes(nodeId);

    if (isCurrentlyExpanded) {
      // Zuklappen über Chevron-Click
      const newExpanded = expandedNodes.filter((id) => id !== nodeId);
      setExpandedNodes(newExpanded);
      localStorage.setItem(EXPANDED_NODES_KEY, JSON.stringify(newExpanded));
    } else {
      // Aufklappen über Chevron-Click
      const newExpanded = [...expandedNodes, nodeId];
      setExpandedNodes(newExpanded);
      localStorage.setItem(EXPANDED_NODES_KEY, JSON.stringify(newExpanded));
    }
  };

  const renderTreeItems = (groups: ClassificationGroupMap[]) => {
    return groups.map((group) => {
      const hasChildren = group.children.length > 0;
      const isExpanded = expandedNodes.includes(group.id);

      return (
        <TreeItem
          key={group.id}
          nodeId={group.id}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
              {hasChildren && (
                <Box
                  onClick={(e) => handleChevronClick(e, group.id)}
                  sx={{
                    'cursor': 'pointer',
                    'display': 'flex',
                    'alignItems': 'center',
                    'mr': 0.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      borderRadius: 1,
                    },
                    'p': 0.25,
                  }}
                >
                  {isExpanded ? (
                    <ExpandMore sx={{ fontSize: 20 }} />
                  ) : (
                    <ChevronRight sx={{ fontSize: 20 }} />
                  )}
                </Box>
              )}
              <Folder sx={{ mr: 1, ml: hasChildren ? 0 : 2.5 }} />
              {group.name}
            </Box>
          }
        >
          {hasChildren && renderTreeItems(group.children)}
        </TreeItem>
      );
    });
  };

  return (
    <Stack>
      <nav aria-label="classification features">
        <List>
          <ListItem onClick={() => navigate(RoutePaths.CLASSIFICATION_FEATURES)}>
            <ListItemButton>
              <ListItemText sx={{ mr: 2 }}>{t('classification.sidebar.features')}</ListItemText>
            </ListItemButton>
          </ListItem>
          <ListItem onClick={() => navigate(RoutePaths.CLASSIFICATION_OBJECTS)}>
            <ListItemButton>
              <ListItemText sx={{ mr: 2 }}>{t('classification.sidebar.objects')}</ListItemText>
            </ListItemButton>
          </ListItem>
        </List>
      </nav>
      <Divider />
      <nav aria-label="classification groups">
        <List>
          <ListItem onClick={() => navigate(RoutePaths.CLASSIFICATION_GROUPS)}>
            <ListItemButton>
              <ListItemText sx={{ mr: 2 }}>
                {t('classification.sidebar.classificationGroups')}
              </ListItemText>
            </ListItemButton>
          </ListItem>
        </List>
        <Box sx={{ pl: 2 }}>
          {isGroupsLoading ? (
            <Box sx={{ p: 2 }}>{t('loading')}</Box>
          ) : (
            <TreeView
              expanded={expandedNodes}
              onNodeToggle={handleToggle}
              onNodeSelect={handleNodeClick}
              selected={''}
            >
              {renderTreeItems(groupMap)}
            </TreeView>
          )}
        </Box>
      </nav>
    </Stack>
  );
}
